import { hasActiveStripeAccount } from '@shared/utils/payoutAccount';
import { isUnitedStatesCode } from '@shared/utils/address';
import { DEFAULT_CURRENCY } from '@shared/utils/currency/constants';

type PostStripeReturnInputs = Readonly<{
  checkStripeStatus: string | null;
  fetchingPayoutInfo: boolean;
  payoutStatus: string | undefined;
  preloadedOrderIdForRefund: string | null;
}>;

export type PostStripeReturnAction = 'wait' | 'openSetupDialog' | 'inRefundFlow' | 'skip';

/**
 * Decide what CreditCardSetup should do after returning from Stripe KYC.
 * - 'wait': fetch not settled or status is non-terminal ('pending') — re-run on next deps change.
 * - 'openSetupDialog': KYC completed and this is the non-refund flow.
 * - 'inRefundFlow': KYC completed but the refund flow owns the post-return UX.
 * - 'skip': back-out — terminal non-success status.
 */
export const decidePostStripeReturnAction = (inputs: PostStripeReturnInputs): PostStripeReturnAction => {
  if (inputs.checkStripeStatus !== 'true') return 'skip';
  if (inputs.fetchingPayoutInfo || !inputs.payoutStatus) return 'wait';
  if (inputs.payoutStatus === 'pending') return 'wait';

  const didCompleteKYC = inputs.payoutStatus === 'readyToUse';
  const isInRefundFlow = !!inputs.preloadedOrderIdForRefund;

  if (didCompleteKYC && !isInRefundFlow) return 'openSetupDialog';
  if (didCompleteKYC && isInRefundFlow) return 'inRefundFlow';
  return 'skip';
};

type RefundReturnInputs = Readonly<{
  preloadedOrderIdForRefund: string | null;
  fetchingPayoutInfo: boolean;
  payoutStatus: string | undefined;
}>;

export type RefundReturnAction = 'wait' | 'noop' | 'openRefundDialog' | 'clearAndSkip';

/**
 * Decide what GiftTracker should do for a Stripe-return URL carrying preloadedOrderIdForRefund.
 * - 'noop': param absent — not in a Stripe-return flow.
 * - 'wait': fetch not settled or 'pending' — re-run on next deps change.
 * - 'openRefundDialog': KYC completed — open the refund handler.
 * - 'clearAndSkip': back-out — clear the param without opening anything.
 */
export const decideRefundReturnAction = (inputs: RefundReturnInputs): RefundReturnAction => {
  if (!inputs.preloadedOrderIdForRefund) return 'noop';
  if (inputs.fetchingPayoutInfo || !inputs.payoutStatus) return 'wait';
  if (inputs.payoutStatus === 'pending') return 'wait';

  return inputs.payoutStatus === 'readyToUse' ? 'openRefundDialog' : 'clearAndSkip';
};

/**
 * Check if account can withdraw funds to bank account
 *
 * Returns true for:
 * - readyToUse: Fully verified, Stripe allows withdrawals
 *
 * Returns false for:
 * - onboarding: Setup incomplete
 * - pending: Verification in progress, Stripe blocks withdrawals
 * - rejected: Account failed verification
 */
export const canWithdrawFunds = (status: string): boolean => {
  return status === 'readyToUse';
};

/**
 * Check if account can enable credit card payments for cash funds
 *
 * With KYC-later flow, accounts in any active state (onboarding, pending, readyToUse)
 * can enable credit card collection. The backend handles contribution holds
 * until verification completes.
 *
 * Without KYC-later, falls back to the stricter canWithdrawFunds check (readyToUse only).
 */
export const canEnableCreditCardForCash = (status: string, kycLaterEnabled: boolean): boolean => {
  return kycLaterEnabled ? hasActiveStripeAccount(status) : canWithdrawFunds(status);
};

type CashEquivalentSupportInputs = Readonly<{
  payoutAccountInfo: { currencyCode?: string | null; countryCode?: string | null } | null;
  registryCurrencyCode?: string;
}>;

/**
 * Get Cash Equivalent is US-only for now. Decide whether it's supported for this registry.
 * - When a payout account exists, validate its own currency (USD) and country (US), tolerating a
 *   missing countryCode so legacy US accounts aren't blocked.
 * - Before an account exists, fall back to the registry's configured currency so pre-setup non-US
 *   hosts are still gated (an undefined currency defaults to USD for legacy US registries).
 */
export const isCashEquivalentSupported = ({ payoutAccountInfo, registryCurrencyCode }: CashEquivalentSupportInputs): boolean => {
  if (payoutAccountInfo) {
    const currencyIsUsd = (payoutAccountInfo.currencyCode?.toUpperCase() ?? DEFAULT_CURRENCY) === DEFAULT_CURRENCY;
    const countryIsUs = !payoutAccountInfo.countryCode || isUnitedStatesCode(payoutAccountInfo.countryCode);
    return currencyIsUsd && countryIsUs;
  }
  return (registryCurrencyCode?.toUpperCase() ?? DEFAULT_CURRENCY) === DEFAULT_CURRENCY;
};
